import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useCart } from "@/context/CartContext";
import { publicApi } from "@/lib/api";
import CartSheet from "@/components/public/CartSheet";
import BrandLogo from "@/components/public/BrandLogo";
import MoonphaseThemeToggle from "@/components/MoonphaseThemeToggle";
import VipPopup from "@/components/public/VipPopup";
import AuthModal from "@/components/public/AuthModal";
import ConciergeWidget from "@/components/public/ConciergeWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Menu, X, UserRound, ShoppingBag, ShieldCheck, LogOut, Search, ArrowRight } from "lucide-react";
import lchPlaque from "@/assets/lch-plaque.png";
import { useSiteNav } from "@/lib/cms";

const SCENE = "https://images.unsplash.com/photo-1547996160-81dfa63595aa?w=1600&q=70";

// Header/menu/footer nav is CMS-driven via useSiteNav() (defaults in @/lib/cms).

const QUICK_BRANDS = ["Rolex", "Patek Philippe", "Audemars Piguet", "Omega", "Tudor", "Cartier"];
const ANNC_KEY = "lch-annc-dismissed";

const Fleur = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 1.4c-.9 1.1-1.3 2.2-1.3 3.3 0 .8.3 1.5.8 2.2-.9-.5-2-.6-2.8-.2-1 .5-1.6 1.5-1.6 2.7 0 1.1.6 2.1 1.6 2.6-.6 0-1.2.2-1.7.6-.8.6-1.2 1.6-1 2.6.2.9.9 1.6 1.8 1.9H8v1.5h3v2.2c-1.1.2-1.9.9-1.9 1.7h5.8c0-.8-.8-1.5-1.9-1.7v-2.2h3v-1.5h-.7c.9-.3 1.6-1 1.8-1.9.2-1-.2-2-1-2.6-.5-.4-1.1-.6-1.7-.6 1-.5 1.6-1.5 1.6-2.6 0-1.2-.6-2.2-1.6-2.7-.8-.4-1.9-.3-2.8.2.5-.7.8-1.4.8-2.2 0-1.1-.4-2.2-1.3-3.3z" />
  </svg>
);

const iconBtn =
  "flex h-10 w-10 items-center justify-center rounded-md border border-[var(--border-lch)] text-[var(--text-2)] transition-colors hover:border-[#c9a86a]/50 hover:text-[#c9a86a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-lch)]";

export default function PublicLayout({ children }) {
  const { customer, logout, openAuthModal } = useCustomerAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const nav = useSiteNav();
  const [annc, setAnnc] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    try { setAnnc(window.localStorage.getItem(ANNC_KEY) !== "1"); } catch (e) { setAnnc(true); }
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Live search suggestions (debounced) while the popover is open.
  useEffect(() => {
    if (!searchOpen) return;
    const t = term.trim();
    if (t.length < 1) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const r = await publicApi.get("/public/watches", { params: { q: t } });
        setResults((r.data || []).slice(0, 6));
      } catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(id);
  }, [term, searchOpen]);

  const dismissAnnc = () => {
    try { window.localStorage.setItem(ANNC_KEY, "1"); } catch (e) { /* ignore */ }
    setAnnc(false);
  };

  const goSearch = (value) => {
    const t = (value ?? term).trim();
    navigate(t ? `/watches?q=${encodeURIComponent(t)}` : "/watches");
    setSearchOpen(false);
    setTerm("");
    setResults([]);
  };

  const goWatch = (id) => {
    navigate(`/watches/${id}`);
    setSearchOpen(false);
    setTerm("");
    setResults([]);
  };

  const priceStr = (w) => (w.price_on_request || w.price == null ? "Price on request" : `$${Number(w.price).toLocaleString()}`);

  const closeMenu = () => setMenuOpen(false);

  const navLinkClass = ({ isActive }) =>
    `relative text-[13px] uppercase tracking-[0.18em] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-[#c9a86a] after:transition-all after:duration-300 ${
      isActive ? "text-[#c9a86a] after:w-full" : "text-[var(--text-2)] hover:text-[var(--text)] after:w-0 hover:after:w-full"
    }`;

  const CartButton = (
    <button onClick={() => setOpen(true)} data-testid="cart-icon-btn" className={`relative ${iconBtn}`} aria-label="Open cart">
      <ShoppingBag size={18} />
      {count > 0 && (
        <span className="absolute -top-2 -right-2 bg-[#c9a86a] text-[var(--on-gold)] text-[10px] font-semibold rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="cart-count">{count}</span>
      )}
    </button>
  );

  return (
    <div className="lch-public lch-grain min-h-screen flex flex-col bg-[var(--bg)] text-[var(--text)]">
      {annc && (
        <div className="relative bg-[var(--panel-2)] border-b border-[var(--border-lch)] text-center" data-testid="military-announcement">
          <div className="max-w-7xl mx-auto px-10 py-2 flex items-center justify-center gap-2 text-[12px] tracking-wide text-[var(--text-2)]">
            <ShieldCheck size={14} className="text-[#c9a86a] shrink-0" />
            <span>{nav.announcement.lead}<span className="text-[#c9a86a] font-medium">{nav.announcement.highlight}</span>{nav.announcement.tail}</span>
          </div>
          <button onClick={dismissAnnc} aria-label="Dismiss" data-testid="dismiss-announcement" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--text)] transition-colors"><X size={14} /></button>
        </div>
      )}

      <header className="sticky top-0 z-40 border-b border-[var(--border-lch)] backdrop-blur-md">
        {/* Background scene — subtle, sits behind the header content */}
        <div className="absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
          <img src={SCENE} alt="" className="absolute inset-0 w-full h-full object-cover object-center opacity-[0.16]" />
          <div className="absolute inset-0 bg-[var(--overlay)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg)]/70 via-transparent to-[var(--bg)]/70" />
        </div>

        <div className={`max-w-7xl mx-auto px-4 sm:px-5 flex items-center gap-3 sm:gap-5 transition-[height] duration-300 ease-out ${scrolled ? "h-16" : "h-24 md:h-28"}`}>
          {/* Left: menu + plaque */}
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <button onClick={() => setMenuOpen(true)} data-testid="nav-menu-trigger" aria-label="Open menu" className={iconBtn}>
              <Menu size={20} />
            </button>
            <Link to="/" className="flex items-center" data-testid="brand-home-link" aria-label="Le Collectif Horloger — Home">
              <img src={lchPlaque} alt="Le Collectif Horloger — by Atelier Dugas" draggable="false"
                className={`w-auto select-none transition-[height] duration-300 ease-out ${scrolled ? "h-10 sm:h-11" : "h-14 md:h-16"}`} />
            </Link>
            <MoonphaseThemeToggle size="h-10" className="ml-1 sm:ml-2 shrink-0" />
          </div>

          {/* Center: primary nav */}
          <nav className="hidden md:flex flex-1 flex-col items-center justify-center gap-2">
            <div className="flex items-center gap-6 lg:gap-9">
              {nav.primary.map((n, i) => <NavLink key={`${n.to}-${i}`} to={n.to} className={navLinkClass}>{n.label}</NavLink>)}
            </div>
            <div className={`flex w-full max-w-sm items-center gap-3 transition-[opacity,max-height] duration-300 ease-out overflow-hidden ${scrolled ? "opacity-0 max-h-0" : "opacity-100 max-h-6"}`} aria-hidden="true">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#c9a86a]/45" />
              <Fleur className="h-3 w-3 text-[#c9a86a]/80" />
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#c9a86a]/45" />
            </div>
          </nav>

          {/* Right: VIP on top of the icon row */}
          <div className={`ml-auto flex shrink-0 ${scrolled ? "flex-row items-center gap-2.5 sm:gap-3" : "flex-col items-end gap-2"}`}>
            <Button
              onClick={() => navigate("/vip")}
              data-testid="join-vip-cta"
              className={`inline-flex items-center gap-2 rounded-md border border-[#c9a86a]/60 bg-[var(--panel-2)] text-[var(--text)] font-semibold tracking-[0.14em] uppercase hover:bg-[var(--panel)] hover:border-[#c9a86a] ${scrolled ? "hidden sm:inline-flex h-9 px-3 text-[11px]" : "h-9 px-4 text-[12px]"}`}
            >
              <Fleur className="h-4 w-4 text-[#c9a86a]" />
              Join the VIP List
            </Button>

            <div className="flex items-center gap-2 sm:gap-2.5">
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <button className={iconBtn} data-testid="header-search-btn" aria-label="Search the collection"><Search size={18} /></button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={10} className="lch-public w-[340px] bg-[var(--panel)] border-[var(--border-2)] text-[var(--text)] p-4">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] mb-2">Search the collection</div>
                  <form onSubmit={(e) => { e.preventDefault(); goSearch(); }} className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                      <Input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Brand, model, or reference…"
                        data-testid="header-search-input" className="pl-9 bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" />
                    </div>
                    <Button type="submit" data-testid="header-search-submit" className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] h-10 w-10 p-0 shrink-0"><ArrowRight size={18} /></Button>
                  </form>

                  {term.trim() ? (
                    <div className="mt-3 max-h-72 overflow-y-auto -mx-1 px-1" data-testid="search-results">
                      {searching && <div className="py-3 text-xs text-[var(--muted)]">Searching…</div>}
                      {!searching && results.length === 0 && (
                        <div className="py-3 text-xs text-[var(--muted)]">No matches. Press enter to browse all timepieces.</div>
                      )}
                      {results.map((w) => (
                        <button key={w.id} onClick={() => goWatch(w.id)} data-testid="search-result"
                          className="w-full flex items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-[var(--panel-2)]">
                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-[var(--bg-2)] border border-[var(--border-lch)]">
                            {(w.public_photos && w.public_photos[0])
                              ? <img src={w.public_photos[0]} alt="" className="h-full w-full object-cover" loading="lazy" />
                              : <div className="flex h-full w-full items-center justify-center text-[var(--muted)]"><Search size={14} /></div>}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-[var(--text)]">{w.brand} {w.model}</div>
                            <div className="truncate text-[11px] text-[var(--muted)]">{w.reference_number ? `Ref ${w.reference_number} · ` : ""}{priceStr(w)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {QUICK_BRANDS.map((b) => (
                        <button key={b} onClick={() => goSearch(b)} data-testid={`search-brand-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="rounded-full border border-[var(--border-lch)] px-2.5 py-1 text-[11px] text-[var(--text-2)] transition-colors hover:border-[#c9a86a]/50 hover:text-[#c9a86a]">{b}</button>
                      ))}
                    </div>
                  )}
                </PopoverContent>
              </Popover>

              <button onClick={() => (customer ? navigate("/account") : openAuthModal())} data-testid="account-link"
                aria-label={customer ? "My account" : "Sign in"} title={customer ? (customer.name || "My account") : "Sign in"} className={iconBtn}>
                <UserRound size={18} />
              </button>

              <span className="hidden sm:block h-6 w-px bg-[var(--border-lch)]" aria-hidden="true" />
              {CartButton}
            </div>
          </div>
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-[#c9a86a]/40 to-transparent" aria-hidden="true" />
      </header>

      {/* Rich slide-in menu */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="lch-public w-[86vw] max-w-sm bg-[var(--panel)] border-r border-[var(--border-2)] text-[var(--text)] p-0 overflow-y-auto">
          <SheetHeader className="px-6 pt-6 pb-4 text-left">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <img src={lchPlaque} alt="Le Collectif Horloger" className="h-14 w-auto object-contain" />
          </SheetHeader>

          <div className="px-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--muted)] mb-3">Explore</div>
            <nav className="grid grid-cols-2 gap-3">
              {nav.primary.map((n, i) => (
                <Link key={`${n.to}-${i}`} to={n.to} onClick={closeMenu} data-testid={`menu-item-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex flex-col justify-between rounded-xl border border-[var(--border-lch)] bg-[var(--panel-2)] p-4 h-24 transition-transform duration-200 hover:-translate-y-0.5 hover:border-[#c9a86a]/50">
                  <Fleur className="h-4 w-4 text-[#c9a86a]" />
                  <span className="font-serif text-lg leading-tight text-[var(--text)] group-hover:text-[#c9a86a] transition-colors">{n.label}</span>
                </Link>
              ))}
            </nav>
          </div>

          <div className="px-6 mt-8">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--muted)] mb-2">{nav.footer.house_title}</div>
            <div className="flex flex-col">
              {nav.more.map((n, i) => (
                <Link key={`${n.to}-${i}`} to={n.to} onClick={closeMenu} data-testid={`menu-item-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center justify-between py-3 border-b border-[var(--border-lch)] text-[var(--text-2)] hover:text-[#c9a86a] transition-colors">
                  <span className="tracking-wide">{n.label}</span>
                  <ArrowRight size={15} className="opacity-50" />
                </Link>
              ))}
            </div>
          </div>

          <div className="px-6 mt-8 pb-8 space-y-3">
            <Button onClick={() => { closeMenu(); navigate("/vip"); }} className="w-full gap-2 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold tracking-[0.14em] uppercase text-xs">
              <Fleur className="h-4 w-4" /> Join the VIP List
            </Button>
            {customer ? (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => { closeMenu(); navigate("/account"); }} data-testid="menu-item-account" className="flex-1 border-[var(--border-2)] text-[var(--text-2)] gap-2"><UserRound size={16} /> My Account</Button>
                <Button variant="outline" onClick={() => { closeMenu(); logout(); navigate("/"); }} data-testid="menu-item-signout" className="border-[var(--border-2)] text-[var(--muted)]"><LogOut size={16} /></Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => { closeMenu(); openAuthModal(); }} data-testid="menu-item-account" className="w-full border-[var(--border-2)] text-[var(--text-2)] gap-2"><UserRound size={16} /> Client Account</Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[var(--border-lch)] bg-[var(--bg-2)]">
        <div className="max-w-7xl mx-auto px-5 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <BrandLogo size="md" showTagline={true} />
            <p className="text-sm text-[var(--muted)] mt-4 max-w-xs">{nav.footer.tagline}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--text-2)] mb-3">{nav.footer.house_title}</div>
            <ul className="space-y-2 text-sm text-[var(--muted)]">
              {nav.footer_links.map((n, i) => (
                <li key={`${n.to}-${i}`}><Link to={n.to} className="hover:text-[#c9a86a]">{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--text-2)] mb-3">{nav.footer.promise_title}</div>
            <p className="text-sm text-[var(--muted)]">{nav.footer.promise_body}</p>
            <p className="text-xs text-[var(--muted)] mt-3 flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#c9a86a]" /> {nav.footer.military_note}</p>
          </div>
        </div>
        <div className="border-t border-[var(--border-lch)] py-5 text-center text-[11px] text-[var(--muted)] tracking-widest">
          © {new Date().getFullYear()} {nav.footer.copyright}
        </div>
      </footer>

      <CartSheet />
      <VipPopup />
      <AuthModal />
      <ConciergeWidget />
    </div>
  );
}
